import React, { useEffect, useRef } from 'react';
import { ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface PricingProps {
  language: string;
}

const Pricing: React.FC<PricingProps> = ({ language }) => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const content = {
    en: {
      title: "Treatment Pricing",
      subtitle: "Premium treatments tailored to enhance your natural beauty",
      bookNow: "Book Now",
      treatments: {
        botox: {
          title: "Botox",
          items: [
            { name: "Smoking wrinkles", price: "550 kr" },
            { name: "Lip lift / Lip flip", price: "550 kr" },
            { name: "Nose wrinkles", price: "550 kr" },
            { name: "Jaw", price: "1650 kr" },
            { name: "Forehead", price: "1650 kr" }
          ]
        },
        fillers: {
          title: "Fillers",
          items: [
            { name: "Nexfill 1 ml", price: "1200 kr" },
            { name: "Monalise 1 ml", price: "1300 kr" },
            { name: "Revolax 1 ml", price: "1500 kr" },
            { name: "Restylane 1 ml", price: "1700 kr" },
            { name: "Juvederm 1 ml", price: "1900 kr" }
          ]
        },
        prp: {
          title: "PRP Therapy",
          items: [
            { name: "Single treatment", price: "1700 kr" },
            { name: "3x treatments (Offer)", price: "4500 kr" }
          ],
          note: "* PRP therapy uses your own blood platelets to stimulate natural skin renewal"
        },
        mesotherapy: {
          title: "Mesotherapy",
          items: [
            { name: "Full Face", price: "2150 kr" },
            { name: "3x Full Face (Offer)", price: "Ask in clinic" }
          ],
          note: "* Deep skin hydration and rejuvenation treatment"
        }
      }
    },
    da: {
      title: "Behandlingspriser",
      subtitle: "Premium behandlinger skræddersyet til at fremhæve din naturlige skønhed",
      bookNow: "Book Nu",
      treatments: {
        botox: {
          title: "Botox",
          items: [
            { name: "Rygerynker", price: "550 kr" },
            { name: "Løft af overlæbe / Lip flip", price: "550 kr" },
            { name: "Rynker på næsen", price: "550 kr" },
            { name: "Kæbe", price: "1650 kr" },
            { name: "Pande", price: "1650 kr" }
          ]
        },
        fillers: {
          title: "Fillers",
          items: [
            { name: "Nexfill 1 ml", price: "1200 kr" },
            { name: "Monalise 1 ml", price: "1300 kr" },
            { name: "Revolax 1 ml", price: "1500 kr" },
            { name: "Restylane 1 ml", price: "1700 kr" },
            { name: "Juvederm 1 ml", price: "1900 kr" }
          ]
        },
        prp: {
          title: "PRP Behandling",
          items: [
            { name: "Enkelt behandling", price: "1700 kr" },
            { name: "3x behandlinger (Tilbud)", price: "4500 kr" }
          ],
          note: "* PRP-behandling bruger dine egne blodplader til at stimulere naturlig hudfornyelse"
        },
        mesotherapy: {
          title: "Mesoterapi",
          items: [
            { name: "Hele ansigtet", price: "2150 kr" },
            { name: "3x Hele ansigtet (Tilbud)", price: "Spørg i klinikken" }
          ],
          note: "* Dyb fugtgivende og foryngende hudbehandling"
        }
      }
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-up');
          }
        });
      },
      { threshold: 0.1 }
    );

    const cards = document.querySelectorAll('.pricing-card');
    cards.forEach((card) => observer.observe(card));

    return () => observer.disconnect();
  }, []);

  const handleBookClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/book');
  };

  return (
    <section className="py-20 bg-[#1A1A1A] relative overflow-hidden" id="pricing" ref={sectionRef}>
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#A68B7C]/30 to-transparent" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-40 h-[2px] bg-gradient-to-r from-transparent via-[#A68B7C]/50 to-transparent" />
      <div className="absolute top-[1px] left-1/2 -translate-x-1/2 w-20 h-[1px] bg-gradient-to-r from-transparent via-[#A68B7C]/30 to-transparent" />

      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(166,139,124,0.15),transparent)]" />
      <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'url("data:image/png;base64,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")' }} />
      
      <div className="container mx-auto px-4 relative">
        <div className="text-center mb-16">
          <h2 
            className="text-3xl md:text-4xl font-serif font-bold mb-4 text-white"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {content[language as keyof typeof content].title}
          </h2>
          <p className="text-lg text-gray-400 max-w-3xl mx-auto">
            {content[language as keyof typeof content].subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Botox Card */}
          <div className="pricing-card opacity-0 group">
            <div className="relative backdrop-blur-md rounded-2xl overflow-hidden transition-all duration-500 bg-gradient-to-r from-[#2A2A2A]/90 to-[#2A2A2A]/80 hover:from-[#2A2A2A] hover:to-[#2A2A2A]/90 border border-[#A68B7C]/20 p-6 shadow-[0_4px_20px_rgb(0,0,0,0.2)] hover:shadow-[0_8px_30px_rgb(166,139,124,0.1)]">
              <h3 className="text-2xl font-serif font-semibold mb-6 text-[#A68B7C]">{content[language as keyof typeof content].treatments.botox.title}</h3>
              <ul className="space-y-4 mb-6">
                {content[language as keyof typeof content].treatments.botox.items.map((item, index) => (
                  <li key={index} className="flex justify-between items-center text-gray-300">
                    <span>{item.name}</span>
                    <span className="font-semibold">{item.price}</span>
                  </li>
                ))}
              </ul>
              <button 
                onClick={handleBookClick}
                className="inline-flex items-center text-[#A68B7C] hover:text-white transition-colors duration-300 group-hover:translate-x-1"
              >
                {content[language as keyof typeof content].bookNow} <ArrowRight size={16} className="ml-2" />
              </button>
            </div>
          </div>

          {/* Fillers Card */}
          <div className="pricing-card opacity-0 group animation-delay-200">
            <div className="relative backdrop-blur-md rounded-2xl overflow-hidden transition-all duration-500 bg-gradient-to-r from-[#2A2A2A]/90 to-[#2A2A2A]/80 hover:from-[#2A2A2A] hover:to-[#2A2A2A]/90 border border-[#A68B7C]/20 p-6 shadow-[0_4px_20px_rgb(0,0,0,0.2)] hover:shadow-[0_8px_30px_rgb(166,139,124,0.1)]">
              <h3 className="text-2xl font-serif font-semibold mb-6 text-[#A68B7C]">{content[language as keyof typeof content].treatments.fillers.title}</h3>
              <ul className="space-y-4 mb-6">
                {content[language as keyof typeof content].treatments.fillers.items.map((item, index) => (
                  <li key={index} className="flex justify-between items-center text-gray-300">
                    <span>{item.name}</span>
                    <span className="font-semibold">{item.price}</span>
                  </li>
                ))}
              </ul>
              <button 
                onClick={handleBookClick}
                className="inline-flex items-center text-[#A68B7C] hover:text-white transition-colors duration-300 group-hover:translate-x-1"
              >
                {content[language as keyof typeof content].bookNow} <ArrowRight size={16} className="ml-2" />
              </button>
            </div>
          </div>

          {/* PRP Card */}
          <div className="pricing-card opacity-0 group animation-delay-400">
            <div className="relative backdrop-blur-md rounded-2xl overflow-hidden transition-all duration-500 bg-gradient-to-r from-[#2A2A2A]/90 to-[#2A2A2A]/80 hover:from-[#2A2A2A] hover:to-[#2A2A2A]/90 border border-[#A68B7C]/20 p-6 shadow-[0_4px_20px_rgb(0,0,0,0.2)] hover:shadow-[0_8px_30px_rgb(166,139,124,0.1)]">
              <h3 className="text-2xl font-serif font-semibold mb-6 text-[#A68B7C]">{content[language as keyof typeof content].treatments.prp.title}</h3>
              <ul className="space-y-4 mb-6">
                {content[language as keyof typeof content].treatments.prp.items.map((item, index) => (
                  <li key={index} className="flex justify-between items-center text-gray-300">
                    <span>{item.name}</span>
                    <span className="font-semibold">{item.price}</span>
                  </li>
                ))}
              </ul>
              <div className="text-sm text-gray-400 mb-6">
                <p>{content[language as keyof typeof content].treatments.prp.note}</p>
              </div>
              <button 
                onClick={handleBookClick}
                className="inline-flex items-center text-[#A68B7C] hover:text-white transition-colors duration-300 group-hover:translate-x-1"
              >
                {content[language as keyof typeof content].bookNow} <ArrowRight size={16} className="ml-2" />
              </button>
            </div>
          </div>

          {/* Mesotherapy Card */}
          <div className="pricing-card opacity-0 group animation-delay-600">
            <div className="relative backdrop-blur-md rounded-2xl overflow-hidden transition-all duration-500 bg-gradient-to-r from-[#2A2A2A]/90 to-[#2A2A2A]/80 hover:from-[#2A2A2A] hover:to-[#2A2A2A]/90 border border-[#A68B7C]/20 p-6 shadow-[0_4px_20px_rgb(0,0,0,0.2)] hover:shadow-[0_8px_30px_rgb(166,139,124,0.1)]">
              <h3 className="text-2xl font-serif font-semibold mb-6 text-[#A68B7C]">{content[language as keyof typeof content].treatments.mesotherapy.title}</h3>
              <ul className="space-y-4 mb-6">
                {content[language as keyof typeof content].treatments.mesotherapy.items.map((item, index) => (
                  <li key={index} className="flex justify-between items-center text-gray-300">
                    <span>{item.name}</span>
                    <span className="italic">{item.price}</span>
                  </li>
                ))}
              </ul>
              <div className="text-sm text-gray-400 mb-6">
                <p>{content[language as keyof typeof content].treatments.mesotherapy.note}</p>
              </div>
              <button 
                onClick={handleBookClick}
                className="inline-flex items-center text-[#A68B7C] hover:text-white transition-colors duration-300 group-hover:translate-x-1"
              >
                {content[language as keyof typeof content].bookNow} <ArrowRight size={16} className="ml-2" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;